import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field, inputStyle } from '../../components/Field';
import { PhotoPicker } from '../../components/PhotoPicker';
import { apiSend, apiUpload, ClientApiError } from '../../api/client';
import { downscaleImage } from '../../lib/image';
import { uploadPhotos } from '../../lib/upload';
import { MAX_PHOTOS, validateReferralInput, type FieldErrors } from '../../../shared/validation';
import type { Referral } from '../../../shared/types';

export function NewReferralScreen() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customerName: '',
    customerAddress: '',
    customerPhone: '',
    customerEmail: '',
    jobDescription: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState('');
  const [progress, setProgress] = useState('');
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBanner('');
    const local = validateReferralInput(form);
    setErrors(local);
    if (Object.keys(local).length > 0) return;

    setBusy(true);
    try {
      setProgress('Submitting referral');
      const { referral } = await apiSend<{ referral: Referral }>('/api/referrals', 'POST', form);

      const { failed } = await uploadPhotos(files, {
        downscale: downscaleImage,
        upload: (blob, filename) => apiUpload(`/api/referrals/${referral.id}/photos`, blob, filename),
        onProgress: (index, total) => setProgress(`Uploading photo ${index + 1} of ${total}`),
      });

      navigate(`/referrals/${referral.id}`, {
        replace: true,
        state: failed > 0 ? { photoWarning: failed } : undefined,
      });
    } catch (caught) {
      if (caught instanceof ClientApiError && caught.fields) {
        setErrors(caught.fields);
      } else {
        setBanner(
          caught instanceof ClientApiError ? caught.message : 'Something went wrong. Please try again.',
        );
      }
      setBusy(false);
      setProgress('');
    }
  }

  return (
    <AppShell title="Add Customer">
      <form onSubmit={submit} noValidate>
        {banner && (
          <div className="error-banner" role="alert">
            {banner}
          </div>
        )}

        <Field label="Customer name" htmlFor="customerName" error={errors.customerName}>
          <input
            id="customerName"
            style={inputStyle}
            autoComplete="off"
            value={form.customerName}
            onChange={(e) => update('customerName', e.target.value)}
          />
        </Field>

        <Field label="Address" htmlFor="customerAddress" error={errors.customerAddress}>
          <input
            id="customerAddress"
            style={inputStyle}
            autoComplete="off"
            value={form.customerAddress}
            onChange={(e) => update('customerAddress', e.target.value)}
          />
        </Field>

        <Field label="Phone number" htmlFor="customerPhone" error={errors.customerPhone}>
          <input
            id="customerPhone"
            type="tel"
            inputMode="tel"
            style={inputStyle}
            value={form.customerPhone}
            onChange={(e) => update('customerPhone', e.target.value)}
          />
        </Field>

        <Field
          label="Email"
          htmlFor="customerEmail"
          error={errors.customerEmail}
          hint="Optional."
        >
          <input
            id="customerEmail"
            type="email"
            inputMode="email"
            style={inputStyle}
            value={form.customerEmail}
            onChange={(e) => update('customerEmail', e.target.value)}
          />
        </Field>

        <Field label="What happened" htmlFor="jobDescription" error={errors.jobDescription}>
          <textarea
            id="jobDescription"
            rows={5}
            style={{ ...inputStyle, minHeight: '7rem', resize: 'vertical' }}
            value={form.jobDescription}
            onChange={(e) => update('jobDescription', e.target.value)}
          />
        </Field>

        <Field label="Photos" htmlFor="photos" hint={`Up to ${MAX_PHOTOS}. Optional.`}>
          <div id="photos">
            <PhotoPicker files={files} onChange={setFiles} max={MAX_PHOTOS} />
          </div>
        </Field>

        {progress && (
          <p className="muted" role="status">
            {progress}
          </p>
        )}

        <Button type="submit" full disabled={busy}>
          {busy ? 'Sending' : 'Send referral'}
        </Button>
      </form>
    </AppShell>
  );
}
